// src/contexts/GoogleAuthContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { gapi } from 'gapi-script';
import { GDEBUG } from '../debug/googleDebug';

const CALENDAR_EVENTS_SCOPE = 'https://www.googleapis.com/auth/calendar.events';
const CALENDAR_READONLY_SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';

/** ✅ Scopes mínimos necesarios */
const SCOPES = [
  'openid',
  'email',
  'profile',
  CALENDAR_EVENTS_SCOPE,       // crear/editar eventos
  CALENDAR_READONLY_SCOPE,     // lectura + freeBusy
].join(' ');

const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'];

export interface GoogleAuthContextProps {
  googleToken: string | null;
  isSignedIn: boolean;
  calendarStatus: 'connected' | 'expired' | 'disconnected';
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  ensureCalendarAuth: () => Promise<string>;
  disconnect: () => Promise<void>;
}

const GoogleAuthContext = createContext<GoogleAuthContextProps | undefined>(undefined);

export const useGoogleAuth = () => {
  const ctx = useContext(GoogleAuthContext);
  if (!ctx) throw new Error('useGoogleAuth must be used within GoogleAuthProvider');
  return ctx;
};

interface ProviderProps { children: ReactNode; }

/** Helper para formatear cualquier error desconocido */
const toMsg = (err: unknown) => {
  if (err && typeof err === 'object') {
    const e = err as any;
    return e.error || e.details || e.message || JSON.stringify(e);
  }
  return String(err);
};

export const GoogleAuthProvider: React.FC<ProviderProps> = ({ children }) => {
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState<
    'connected' | 'expired' | 'disconnected'
  >('disconnected');

  const getStatusFromAuth = (signed: boolean, user?: any) => {
    if (!signed) return 'disconnected' as const;
    const resp = user?.getAuthResponse?.(true);
    if (!resp?.access_token) return 'disconnected' as const;
    if (resp?.expires_at && Date.now() >= resp.expires_at - 30_000) {
      return 'expired' as const;
    }
    return 'connected' as const;
  };

 useEffect(() => {
  function init() {
    GDEBUG.group('gapi.load init');
    GDEBUG.log('clientId:', process.env.REACT_APP_GOOGLE_CLIENT_ID);
    GDEBUG.log('SCOPES solicitados:', SCOPES);
    GDEBUG.log('DISCOVERY_DOCS:', DISCOVERY_DOCS);

    (async () => {
      try {
        await gapi.client.init({
          clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          scope: SCOPES,
          discoveryDocs: DISCOVERY_DOCS,
        });
        GDEBUG.log('gapi.client.init OK');

        try {
          await gapi.client.load('calendar', 'v3');
          GDEBUG.log('calendar v3 cargado');
        } catch (e: unknown) {
          GDEBUG.warn('No se pudo cargar calendar v3 (continuo):', toMsg(e));
        }

        const auth = gapi.auth2.getAuthInstance();

        // Cambios de usuario
        auth.currentUser.listen((u: any) => {
          GDEBUG.group('auth.currentUser.listen');
          try {
            const scopes = u?.getGrantedScopes?.() || '';
            const token = u?.getAuthResponse?.()?.access_token;
            GDEBUG.log('grantedScopes:', scopes);
            GDEBUG.log('isSignedIn:', auth.isSignedIn.get());
            GDEBUG.log('profile:', u?.getBasicProfile?.()?.getEmail?.());
            setIsSignedIn(auth.isSignedIn.get());
            const tok = token || null;
            setGoogleToken(tok);
            if (tok) gapi.client.setToken({ access_token: tok });
            GDEBUG.checkTokenInfo(tok);
          } catch (e: unknown) {
            GDEBUG.error('Error en currentUser.listen:', toMsg(e));
          }
          GDEBUG.groupEnd();
        });

        const refresh = () => {
          GDEBUG.group('refresh()');
          const u = auth.currentUser.get();
          const tok = u?.getAuthResponse()?.access_token || null;
          const scopes = u?.getGrantedScopes?.() || '';
          const signed = auth.isSignedIn.get();
          GDEBUG.log('isSignedIn:', auth.isSignedIn.get());
          GDEBUG.log('grantedScopes:', scopes);
          setGoogleToken(tok);
          setIsSignedIn(signed);
          setCalendarStatus(getStatusFromAuth(signed, u));
          if (tok) gapi.client.setToken({ access_token: tok });
          GDEBUG.checkTokenInfo(tok);
          GDEBUG.groupEnd();
        };

        // Estado inicial + listener
        refresh();
        auth.isSignedIn.listen(() => {
          GDEBUG.log('auth.isSignedIn.listen disparado');
          refresh();
        });

        // 🔎 Probe silencioso: valida que el token realmente sirve
        try {
          const probe = await verifyCalendarAccess();
          if (probe.ok) {
            setIsSignedIn(true);
            setCalendarStatus('connected');
          } else {
            GDEBUG.warn('probe Calendar falló:', probe.errorText);
            setIsSignedIn(false);
            setGoogleToken(null);
            setCalendarStatus('disconnected');
            (gapi.client as any).setToken(null);
          }
        } catch (e) {
          GDEBUG.warn('probe error:', toMsg(e));
        }
      } catch (err: unknown) {
        GDEBUG.error('gapi.client.init ERROR:', toMsg(err));
      } finally {
        GDEBUG.groupEnd();
      }
    })();
  }
  gapi.load('client:auth2', init);
}, []);

  useEffect(() => {
    if (!isSignedIn) {
      setCalendarStatus('disconnected');
      return;
    }

    const id = window.setInterval(async () => {
      try {
        const auth = gapi.auth2.getAuthInstance();
        const user = auth.currentUser.get();
        const resp = user?.getAuthResponse?.(true);
        const signed = auth.isSignedIn.get();
        if (!signed || !resp?.access_token) {
          setCalendarStatus('disconnected');
          setGoogleToken(null);
          return;
        }

        if (resp.expires_at && Date.now() >= resp.expires_at - 30_000) {
          try {
            const renewed = await user.reloadAuthResponse();
            const newToken = renewed?.access_token || null;
            setGoogleToken(newToken);
            if (newToken) {
              gapi.client.setToken({ access_token: newToken });
              setCalendarStatus('connected');
            } else {
              setCalendarStatus('expired');
            }
          } catch {
            setCalendarStatus('expired');
          }
          return;
        }

        setCalendarStatus('connected');
      } catch {
        setCalendarStatus('expired');
      }
    }, 60_000);

    return () => window.clearInterval(id);
  }, [isSignedIn]);


  // helper: comprueba que el token sirve para Calendar y saca el email (si está)
async function verifyCalendarAccess(): Promise<{ ok: boolean; email?: string; errorText?: string }> {
  const tok = (gapi.client as any)?.getToken?.()?.access_token;
  if (!tok) return { ok: false, errorText: 'Sin access_token tras signIn' };

  // ping a calendarList para verificar permisos reales
  const r = await fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList?maxResults=1', {
    headers: { Authorization: `Bearer ${tok}` },
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => String(r.status));
    return { ok: false, errorText: `Calendar ${r.status}: ${txt}` };
  }

  try {
    const ti = await fetch(`https://oauth2.googleapis.com/tokeninfo?access_token=${tok}`).then(rr => rr.json());
    return { ok: true, email: (ti as any).email };
  } catch {
    return { ok: true };
  }
}


const signIn = async () => {
  const auth = gapi.auth2.getAuthInstance();
  GDEBUG.group('signIn()');

  const tryPopup = async () => auth.signIn({
    scope: SCOPES,
    prompt: 'consent',
    include_granted_scopes: true as any,
  });


const fallbackRedirect = async () => {
  // fuerza un redirect fijo según entorno (no uses window.location.href)
  const REDIRECT =
    process.env.NODE_ENV === 'production'
      ? 'https://finconnect.secureuserarea.com/crm/AgendaComercial'
      : 'http://localhost:3000/crm/AgendaComercial';

  const CLEAN = REDIRECT.trim(); // asegúrate de que no hay espacios
  GDEBUG.warn('Popup falló/bloqueado → ux_mode=redirect');
  GDEBUG.log('-> redirect_uri que voy a enviar:', CLEAN);
  GDEBUG.log('-> clientId usado:', process.env.REACT_APP_GOOGLE_CLIENT_ID);

  await auth.signIn({
    scope: SCOPES,
    prompt: 'consent',
    include_granted_scopes: true as any,
    ux_mode: 'redirect' as any,
    redirect_uri: CLEAN,
  });
};



  try {
    const user: any = await tryPopup(); // 1) intentamos popup
    GDEBUG.log('Intentando signIn por popup con clientId:', process.env.REACT_APP_GOOGLE_CLIENT_ID);
    const tok = user.getAuthResponse().access_token;
    gapi.client.setToken({ access_token: tok });
    setGoogleToken(tok);

    // 2) verificación real contra Calendar
    const check = await verifyCalendarAccess();
    if (!check.ok) {
      GDEBUG.warn('Token obtenido pero sin acceso efectivo:', check.errorText);
      await fallbackRedirect();
      return; // tras redirect no seguimos
    }

    setIsSignedIn(true);
    setIsSignedIn(true);
GDEBUG.log('Conectado OK como:', user.getBasicProfile()?.getEmail?.() || check.email);
GDEBUG.checkTokenInfo(tok);

// ⬇️ fuerza ir a /crm/AgendaComercial
if (window.location.pathname !== '/crm/AgendaComercial') {
  window.location.assign('/crm/AgendaComercial');   // o history.replaceState si prefieres no dejar rastro
}
    GDEBUG.log('Conectado OK como:', user.getBasicProfile()?.getEmail?.() || check.email);
    GDEBUG.checkTokenInfo(tok);
  } catch (err: any) {
    const code = err?.error || err?.details || err?.message;
    GDEBUG.error('signIn popup ERROR:', code, err);

    if (code === 'popup_blocked_by_browser' || code === 'popup_closed_by_user') {
      await fallbackRedirect();
      return;
    }

    alert(
      'No se pudo conectar con Google.\n\n' +
      'Si estás en incógnito, permite ventanas emergentes y cookies de terceros.\n' +
      'Luego recarga y vuelve a intentar.'
    );
  } finally {
    GDEBUG.groupEnd();
  }
};


  const signOut = async () => {
    const auth = gapi.auth2.getAuthInstance();
    GDEBUG.group('signOut()');
    try {
      await auth.signOut();
      GDEBUG.log('signOut OK');
      setGoogleToken(null);
      setIsSignedIn(false);
      setCalendarStatus('disconnected');
      gapi.client.setToken(null as any);
    } catch (err: unknown) {
      GDEBUG.error('signOut ERROR:', toMsg(err));
    } finally {
      GDEBUG.groupEnd();
    }
  };

  const disconnect = async (): Promise<void> => {
    GDEBUG.group('disconnect()');
    try {
      const auth = gapi.auth2.getAuthInstance();
      await auth.disconnect();
      GDEBUG.log('disconnect OK');
    } catch (e: unknown) {
      GDEBUG.warn('Error al revocar sesión de Google:', toMsg(e));
    } finally {
      setGoogleToken(null);
      setIsSignedIn(false);
      setCalendarStatus('disconnected');
      gapi.client.setToken(null as any);
      GDEBUG.groupEnd();
    }
  };

  /** Verifica y, si falta, pide sólo los scopes mínimos */
  const ensureCalendarAuth = async (): Promise<string> => {
    GDEBUG.group('ensureCalendarAuth()');
    const auth = gapi.auth2.getAuthInstance();
    let user = auth.currentUser.get();

    const has = (s: string) =>
      typeof (user as any).hasGrantedScopes === 'function'
        ? (user as any).hasGrantedScopes(s)
        : (user?.getGrantedScopes?.() || '').includes(s);

    const needEvents = !has(CALENDAR_EVENTS_SCOPE);
    const needReadonly = !has(CALENDAR_READONLY_SCOPE);
    GDEBUG.log('needEvents:', needEvents, 'needReadonly:', needReadonly);

    if (needEvents || needReadonly) {
      await auth.signIn({
        scope: `${CALENDAR_EVENTS_SCOPE} ${CALENDAR_READONLY_SCOPE}`,
        prompt: 'consent',
        include_granted_scopes: true as any,
      });
      user = auth.currentUser.get();
    }

    const token = user.getAuthResponse(true)?.access_token;
    if (!token) {
      const msg = 'No se pudo obtener access_token de Google';
      GDEBUG.error(msg);
      GDEBUG.groupEnd();
      throw new Error(msg);
    }
    gapi.client.setToken({ access_token: token });
    setCalendarStatus('connected');
    try {
      await gapi.client.load('calendar', 'v3');
    } catch (e: unknown) {
      GDEBUG.warn('calendar v3 no cargó (continuo):', toMsg(e));
    }
    GDEBUG.checkTokenInfo(token);
    GDEBUG.groupEnd();
    return token;
  };

  return (
    <GoogleAuthContext.Provider
      value={{
        googleToken,
        isSignedIn,
        calendarStatus,
        signIn,
        signOut,
        ensureCalendarAuth,
        disconnect,
      }}
    >
      {children}
    </GoogleAuthContext.Provider>
  );
};
